import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { Can } from '../../contexts/PermissionsContext'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Textarea } from '../ui/textarea'
import { EmptyState } from '../ui/empty-state'
import { AppIcon } from '../ui/icon'
import { ResolutionPanel } from './ResolutionPanel'
import { CollapsibleText } from './CollapsibleText'
import { VoteBar } from './VoteBar'
import { subjectIcon } from './assembly-icons'
import { apiErrorMessage, useAssemblyChildren, useAssemblyMutations } from './assembly-api'
import {
    MAJORITY_RULES,
    MAX_TITLE_LENGTH,
    OUTCOME_FILTERS,
    RESOLUTION_SUBJECTS,
    clauseText,
    filterResolutions,
    moveItem,
    outcomeTone,
    resolutionTitle,
    ruleShort,
} from './assembly-utils'

type Computed = { result?: string; voices_for?: number; voices_against?: number; voices_abstain?: number }

/**
 * The resolutions of the minutes in agenda order, one card each: number, title,
 * majority, subject, outcome and the voices as a bar. The legal text is behind
 * a "Show text" disclosure. Search words and the outcome filter live in the URL.
 * Spec 2026-10-05-01 §4, §8; spec 2026-10-10-08 §6.
 */
export function ResolutionsTab({
    org,
    minutesUid,
    closed: closedProp,
    entryLocked = false,
    convened = false,
    held = true,
    selected,
    q = '',
    outcome = '',
}: {
    org: string
    minutesUid: string
    closed: boolean
    entryLocked?: boolean
    /** The platform assembly left the draft: the agenda is frozen. */
    convened?: boolean
    /** The meeting took place (or a recorded minutes dated in the past): else votes are not yet due. */
    held?: boolean
    selected?: string
    q?: string
    outcome?: string
}) {
    const closed = closedProp || convened
    const { t } = useTranslation('assemblies')
    const navigate = useNavigate()
    const { data, isLoading } = useAssemblyChildren(org, minutesUid)
    const { createChild, reorder } = useAssemblyMutations(org)
    const children = data?.data ?? []
    const path = { orgCode: org, contractUid: minutesUid }
    const filtered = filterResolutions(children, q, outcome)
    const filtering = !!q.trim() || !!outcome

    const [title, setTitle] = useState('')
    const [text, setText] = useState('')
    const [showText, setShowText] = useState(false)
    const [rule, setRule] = useState<string>('art24')
    const [subject, setSubject] = useState<string>('other')
    const error = apiErrorMessage(createChild.error || reorder.error)

    const setSearch = (next: { resolution?: string; q?: string; outcome?: string }) =>
        navigate({
            to: '/$org/assemblies/$uid',
            params: { org, uid: minutesUid },
            search: (prev: Record<string, unknown>) => ({
                ...prev,
                tab: undefined,
                resolution: 'resolution' in next ? next.resolution : (prev.resolution as string | undefined),
                q: 'q' in next ? next.q || undefined : (prev.q as string | undefined),
                outcome: ('outcome' in next ? next.outcome || undefined : prev.outcome) as 'adopted' | undefined,
            }),
            replace: true,
        })
    const open = (uid?: string) => setSearch({ resolution: uid })

    const add = () => {
        if (!title.trim()) return
        createChild.mutate(
            {
                params: { path },
                body: {
                    clauses: {
                        resolution_title: title.trim(),
                        ...(text.trim() ? { resolution_text: text.trim() } : {}),
                        majority_rule: rule,
                        resolution_subject: subject,
                    },
                },
            },
            { onSuccess: (created) => { setTitle(''); setText(''); setShowText(false); open(created.uid) } },
        )
    }

    const move = (index: number, delta: -1 | 1) => {
        const next = moveItem(children, index, delta)
        if (next === children) return
        reorder.mutate({ params: { path }, body: { child_uids: next.map((c) => c.uid) } })
    }

    if (isLoading) return <p className="text-sm text-muted-foreground">{t('loading')}</p>

    return (
        <div className="space-y-4" data-testid="resolutions-tab">
            {error && <p className="text-sm text-destructive" data-testid="resolutions-error">{error}</p>}

            {children.length > 0 && (
                <div className="flex flex-wrap items-center gap-2" data-testid="resolutions-filters">
                    <Input
                        className="h-9 max-w-xs"
                        value={q}
                        placeholder={t('resolutions.search')}
                        aria-label={t('resolutions.search')}
                        onChange={(e) => setSearch({ q: e.target.value })}
                        data-testid="resolutions-search"
                    />
                    <select
                        aria-label={t('resolutions.outcome_filter')}
                        value={outcome}
                        onChange={(e) => setSearch({ outcome: e.target.value })}
                        className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                        data-testid="resolutions-outcome-filter"
                    >
                        <option value="">{t('resolutions.all_outcomes')}</option>
                        {OUTCOME_FILTERS.map((o) => <option key={o} value={o}>{t(`outcome.${o}`)}</option>)}
                    </select>
                    {filtering && (
                        <span className="text-xs text-muted-foreground" data-testid="resolutions-count">
                            {t('resolutions.count', { shown: filtered.length, total: children.length })}
                        </span>
                    )}
                </div>
            )}

            {children.length === 0 ? (
                <EmptyState variant="panel" title={t('resolutions.empty.title')} description={t('resolutions.empty.description')} data-testid="resolutions-empty" />
            ) : filtered.length === 0 ? (
                <EmptyState variant="panel" title={t('resolutions.no_match')} data-testid="resolutions-no-match" />
            ) : (
                <div className="space-y-2" data-testid="resolutions-list">
                    {filtered.map((c) => {
                        const index = children.findIndex((x) => x.uid === c.uid)
                        const recorded = clauseText(c.clauses, 'recorded_outcome')
                        const computed = c.clauses?.computed_outcome as Computed | undefined
                        const rule = clauseText(c.clauses, 'majority_rule')
                        const subj = clauseText(c.clauses, 'resolution_subject')
                        const legal = clauseText(c.clauses, 'resolution_text')
                        return (
                            <Card
                                key={c.uid}
                                className={selected === c.uid ? 'ring-2 ring-primary/40' : ''}
                                data-testid={`resolution-row-${c.uid}`}
                            >
                                <CardContent className="flex flex-wrap items-start gap-3 p-4">
                                    <span
                                        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold tabular-nums"
                                        data-testid={`resolution-number-${c.uid}`}
                                    >
                                        {c.resolution_number ?? index + 1}
                                    </span>
                                    <div className="min-w-0 flex-1 space-y-2">
                                        <button
                                            type="button"
                                            className="block max-w-full truncate text-left font-medium hover:underline"
                                            onClick={() => open(selected === c.uid ? undefined : c.uid)}
                                            data-testid={`resolution-open-${c.uid}`}
                                        >
                                            <span data-testid={`resolution-title-${c.uid}`}>{resolutionTitle(c)}</span>
                                        </button>
                                        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                                            {subj && (
                                                <span className="inline-flex items-center gap-1" data-testid={`resolution-subject-${c.uid}`}>
                                                    <AppIcon path={subjectIcon(subj)} size="xs" tone="muted" />
                                                    {t(`subject.${subj}`, { defaultValue: subj })}
                                                </span>
                                            )}
                                            {ruleShort(rule) && (
                                                <Badge variant="outline" title={t(`rule.${rule}`)} data-testid={`resolution-rule-${c.uid}`}>
                                                    {ruleShort(rule)}
                                                </Badge>
                                            )}
                                            {c.flags?.includes('to_approve') && <Badge variant="warning">{t('to_approve')}</Badge>}
                                            {c.flags?.includes('votes_aggregate_only') && (
                                                <Badge variant="outline" data-testid={`resolution-aggregate-${c.uid}`}>{t('resolutions.aggregate_only')}</Badge>
                                            )}
                                        </div>
                                        <CollapsibleText text={legal} testId={`resolution-text-${c.uid}`} />
                                    </div>
                                    <div className="flex shrink-0 flex-col items-end gap-2">
                                        <div className="flex items-center gap-2">
                                            {computed?.result && computed.result !== 'none' && computed.result !== recorded && (
                                                <Badge variant="outline" data-testid={`resolution-computed-${c.uid}`}>
                                                    {t('resolutions.computed', { result: t(`outcome.${computed.result}`) })}
                                                </Badge>
                                            )}
                                            {recorded ? (
                                                <Badge variant={outcomeTone(recorded)} data-testid={`resolution-recorded-${c.uid}`}>
                                                    {t(`outcome.${recorded}`)}
                                                </Badge>
                                            ) : (
                                                <Badge variant="secondary" data-testid={`resolution-pending-${c.uid}`}>
                                                    {t(held || closed ? 'outcome.not_voted' : 'outcome.on_agenda')}
                                                </Badge>
                                            )}
                                        </div>
                                        <VoteBar
                                            voicesFor={computed?.voices_for}
                                            voicesAgainst={computed?.voices_against}
                                            voicesAbstain={computed?.voices_abstain}
                                            testId={`resolution-bar-${c.uid}`}
                                        />
                                        <Can perm="contracts.write">
                                            <div className="flex">
                                                <Button variant="ghost" size="sm" disabled={closed || filtering || index === 0} onClick={() => move(index, -1)} aria-label={t('resolutions.move_up')} data-testid={`resolution-up-${c.uid}`}>↑</Button>
                                                <Button variant="ghost" size="sm" disabled={closed || filtering || index === children.length - 1} onClick={() => move(index, 1)} aria-label={t('resolutions.move_down')} data-testid={`resolution-down-${c.uid}`}>↓</Button>
                                            </div>
                                        </Can>
                                    </div>
                                </CardContent>
                            </Card>
                        )
                    })}
                </div>
            )}

            {selected && children.some((c) => c.uid === selected) && (
                <ResolutionPanel key={selected} org={org} minutesUid={minutesUid} resolutionUid={selected} closed={closedProp} entryLocked={entryLocked} />
            )}

            <Can perm="contracts.write">
                {convened && !closedProp ? (
                    <p className="text-sm text-muted-foreground" data-testid="agenda-frozen-hint">{t('resolutions.agenda_frozen')}</p>
                ) : (
                <Card>
                    <CardHeader>
                        <CardTitle className="text-base">{t('resolutions.add_title')}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        <div>
                            <Label htmlFor="resolution-title">{t('resolutions.title_label')}</Label>
                            <Input
                                id="resolution-title"
                                value={title}
                                maxLength={MAX_TITLE_LENGTH}
                                placeholder={t('resolutions.title_placeholder')}
                                onChange={(e) => setTitle(e.target.value)}
                                disabled={closed}
                                data-testid="resolution-add-title"
                            />
                        </div>
                        {showText ? (
                            <div>
                                <Label htmlFor="resolution-text">{t('resolutions.text')}</Label>
                                <Textarea id="resolution-text" value={text} onChange={(e) => setText(e.target.value)} disabled={closed} data-testid="resolution-add-text" />
                            </div>
                        ) : (
                            <button
                                type="button"
                                className="text-xs font-medium text-primary underline-offset-2 hover:underline"
                                onClick={() => setShowText(true)}
                                data-testid="resolution-add-text-toggle"
                            >
                                {t('resolutions.add_text')}
                            </button>
                        )}
                        <div className="flex flex-wrap gap-3">
                            <div>
                                <Label htmlFor="resolution-rule">{t('resolutions.rule')}</Label>
                                <select id="resolution-rule" value={rule} onChange={(e) => setRule(e.target.value)} disabled={closed}
                                    className="block h-9 rounded-md border border-input bg-background px-2 text-sm" data-testid="resolution-add-rule">
                                    {MAJORITY_RULES.map((r) => <option key={r} value={r}>{t(`rule.${r}`)}</option>)}
                                </select>
                            </div>
                            <div>
                                <Label htmlFor="resolution-subject">{t('resolutions.subject')}</Label>
                                <select id="resolution-subject" value={subject} onChange={(e) => setSubject(e.target.value)} disabled={closed}
                                    className="block h-9 rounded-md border border-input bg-background px-2 text-sm" data-testid="resolution-add-subject">
                                    {RESOLUTION_SUBJECTS.map((s) => <option key={s} value={s}>{t(`subject.${s}`)}</option>)}
                                </select>
                            </div>
                        </div>
                        <Button size="sm" onClick={add} disabled={closed || !title.trim() || createChild.isPending} data-testid="resolution-add">
                            {t('resolutions.add')}
                        </Button>
                    </CardContent>
                </Card>
                )}
            </Can>
        </div>
    )
}
